import { ELAKAY_SITE_URL } from "../config";

// Centralized resolution for the "userAddingRoleByToken" flow (/user_Shoosing/:token).
//
// The business token used by this flow lives on User.ativator[].token (server_elakay,
// see elakay_models/user.js + elakay_middleware/authAddingUser.js) — it is NOT the
// connection JWT. This module is the single place that decides which token value is
// "active" for the page, so every consumer (the page itself, Community Management,
// View Your Users, the Footer link, ...) agrees on the same value and the same rules.
//
// Priority order (mission 1.1):
//   1. A valid token present in the URL param.
//   2. A valid token previously saved in localStorage.
//   3. No token available.
//
// "localStorage" (not "localhost") is the only persistence used here, matching the
// key already written elsewhere in the app ("token_userAdding").

export const USER_ADDING_ROLE_TOKEN_STORAGE_KEY = "token_userAdding";

// A value counts as a real token only if it's a non-empty string and not one of the
// classic "looks empty but isn't" foot-guns that produced /user_Shoosing/undefined.
export const isValidUserAddingRoleToken = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value !== "undefined" &&
  value !== "null";

/**
 * Resolve the single active token for the userAddingRoleByToken flow.
 * @param {string|undefined|null} urlToken - value read from useParams().token
 * @returns {string|null} the active token, or null when none is available
 */
export const resolveUserAddingRoleToken = (urlToken) => {
  if (isValidUserAddingRoleToken(urlToken)) return urlToken;

  if (typeof window === "undefined" || !window.localStorage) return null;
  const stored = window.localStorage.getItem(USER_ADDING_ROLE_TOKEN_STORAGE_KEY);
  return isValidUserAddingRoleToken(stored) ? stored : null;
};

/**
 * Persist a freshly-seen URL token as the new fallback, replacing whatever was
 * saved before, but only when it is actually valid and different from what's stored.
 * @param {string|undefined|null} urlToken
 */
export const persistUserAddingRoleTokenIfNeeded = (urlToken) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (!isValidUserAddingRoleToken(urlToken)) return;

  const stored = window.localStorage.getItem(USER_ADDING_ROLE_TOKEN_STORAGE_KEY);
  if (stored !== urlToken) {
    window.localStorage.setItem(USER_ADDING_ROLE_TOKEN_STORAGE_KEY, urlToken);
  }
};

/**
 * Build a safe path to the userAddingRoleByToken page. Never returns a path
 * containing "undefined"/"null" — returns null instead so callers can disable
 * the link/button rather than navigate to a broken URL.
 * @param {string|undefined|null} token
 * @returns {string|null}
 */
export const buildUserAddingRoleTokenPath = (token) =>
  isValidUserAddingRoleToken(token) ? `/user_Shoosing/${encodeURIComponent(token)}` : null;

/**
 * Build the absolute public activation URL from REACT_APP_ELAKAY_URL through
 * ELAKAY_SITE_URL. The shared resolver also protects a public deployment made
 * accidentally with a localhost value by falling back to the page's public
 * origin. Returns "" when the token is unusable.
 * @param {string|undefined|null} activatorToken
 * @returns {string}
 */
export const buildUserChoosingUrl = (activatorToken) => {
  const path = buildUserAddingRoleTokenPath(activatorToken);
  if (!path) return "";
  return `${ELAKAY_SITE_URL}${path}`;
};

/**
 * Selects the ativator[] entry that represents the currently-relevant
 * activation invite, from a User-shaped object (either a full user document
 * that legitimately has `.ativator` populated, or a lightweight
 * `{ ativator: [...] }` wrapper built from a narrow endpoint's response --
 * see server_elakay's getUserAddingRoleToken()/authAddingUser.js, which this
 * mirrors: prefer the most recent still-pending (ativated:false), unexpired
 * entry; fall back to the most recent entry overall so the UI can still show
 * "already used"/"expired" instead of nothing.
 *
 * Never reads user.tokens, userActiveRole.role, or any connection JWT --
 * the activation token only ever lives at user.ativator[].token.
 * @param {{ativator?: Array<{token?: string, ativated?: boolean, experyDate?: string|Date}>}} user
 * @returns {{token: string, ativated?: boolean, experyDate?: string|Date}|null}
 */
export const getValidUserActivator = (user) => {
  if (!user || !Array.isArray(user.ativator)) return null;

  const usable = user.ativator.filter((item) => isValidUserAddingRoleToken(item?.token));
  if (!usable.length) return null;

  const now = Date.now();
  const isPending = (item) => {
    if (item.ativated !== false) return false;
    if (!item.experyDate) return true;
    const expiration = new Date(item.experyDate).getTime();
    return !Number.isFinite(expiration) || expiration > now;
  };

  // Most recent first (ativator is append-only, see User.ativatorTokenUserAdding).
  const reversed = [...usable].reverse();
  return reversed.find(isPending) || reversed[0];
};

// Query param carrying the invitation page to return to after signing in
// (mission 13.2) -- read by Signin.js, written wherever this flow links out
// to /signin. Only ever a same-origin relative path, never an external URL.
export const RETURN_URL_QUERY_PARAM = "returnUrl";

/**
 * Validates a return-URL candidate before it's ever used for navigation:
 * must be a relative, in-app path (starts with a single "/", never "//" —
 * which browsers treat as protocol-relative and would silently redirect off
 * -site). This is the one guard standing between a query param and
 * `navigate()`, so it fails closed (returns null) on anything suspicious.
 * @param {string|undefined|null} value
 * @returns {string|null}
 */
export const getSafeReturnUrl = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return null;
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
  // Browsers read "\" as "/" ("/\evil.com" -> "//evil.com"), and control
  // characters can hide one: refuse both, then require that the value still
  // resolves on this very origin.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(trimmed)) return null;
  try {
    const base = "https://elakay.invalid";
    if (new URL(trimmed, base).origin !== base) return null;
  } catch (_) {
    return null;
  }
  return trimmed;
};
